import { ColorMaps } from '@core/models/user';
import { Worker } from '@core/models/workers';
import { Product } from '@hooks/queries/useProducts';
import { Actions } from '@redux/actions';
import { store } from '@redux/store';
import { Field } from '@state/regions/region.models';
import { Feature, MultiPolygon, Polygon } from '@turf/turf';
import { create } from 'zustand';
import {
  PrescriptionType,
  PrescriptionZoneProperties,
  ProductState,
  ProductUnit,
} from '../utils/prescriptions.models';
import { PRESCRIPTIONS_PRODUCT_UNITS } from './Steps/PrescriptionCreateThirdStep/PrescriptionCreateUnits';

export const CREATE_STEPS = [
  'type_and_location',
  'zone_creation',
  'zone_rates',
  'products',
  'review',
] as const;

export const EDIT_STEPS = ['zone_rates', 'products', 'review'] as const;

export type ProductInfoStatus = 'idle' | 'fulfilled' | 'empty';
export type ProductInfo = {
  product?: Product;
  rate?: number;
  totalQuantity?: number;
  status: ProductInfoStatus;
};
export interface PrescriptionCreateState {
  step: number;
  // Step 1 state
  visualizedSteps: Record<typeof CREATE_STEPS[number], boolean>;
  prescriptionType?: PrescriptionType;
  product?: Product;
  productState: ProductState;
  fields: Field[];
  assignee?: Worker;
  // Step 2 state
  zoneMethod: 'handDrawn' | 'color' | 'fileUpload';
  colorMap: ColorMaps | 'custom';
  customColorMap?: ColorMaps;
  customColorSliderValues: number[];
  uploadedFile: File | null;
  // Step 3 state
  zonesProductRate: { [key: number]: number };
  productUnit: ProductUnit;
  zones?: Feature<Polygon | MultiPolygon, PrescriptionZoneProperties>[];
  zonesPaletteKey?: string;
  zonesHistory: Array<PrescriptionCreateState['zones']>;
  visualizeMode: 'zones' | 'ndvi' | 'visual' | 'contrast';
  highlightZoneDN?: number | null;
  // Step 4 state
  calculatedByOption: 'hectares' | '100l' | 'tank';
  products: Map<string | undefined, ProductInfo>;
  // Breadcrumb actions
  productsWereReset: boolean;
  zonesWereReset: boolean;
}

interface PrescriptionCreateActions {
  goBack: () => void;
  goNext: () => void;
  goToStep: (step: number) => void;
  setProductState: (newState: ProductState) => void;
  setProductsList: (productsMap: Map<string | undefined, ProductInfo>) => void;
  changeProduct: (newProduct: Product, oldProduct: Product | undefined) => void;
  changeProductValue: (productId?: string, newRate?: number, newTotalQuantity?: number) => void;
  changeProductStatus: (productId: string | undefined, newStatus: ProductInfoStatus) => void;
  addProduct: () => void;
  removeProduct: (productId: string | undefined) => void;
  setProductUnit: (newProductUnit: ProductUnit | undefined) => void;
  setFields: (newFields: Field[]) => void;
  toggleField: (field: Field) => void;
  setAssignee: (worker?: Worker) => void;
  setZonesProductRate: (newZonesProductRate: { [key: number]: number } | undefined) => void;
  setType: (newType: PrescriptionType | undefined) => void;
  setZoneMethod: (value: PrescriptionCreateState['zoneMethod']) => void;
  setUploadedFile: (file: File | null) => void;
  setColorMap: (value: PrescriptionCreateState['colorMap']) => void;
  setCustomColorMap: (value: PrescriptionCreateState['customColorMap']) => void;
  setCustomColorSliderValues: (value: number[]) => void;
  setZones: (value: PrescriptionCreateState['zones']) => void;
  setGeneratedZones: (value: PrescriptionCreateState['zones'], paletteKey?: string) => void;
  setVisualizeMode: (value: PrescriptionCreateState['visualizeMode']) => void;
  setVisualizedSteps: (step: typeof CREATE_STEPS[number], value: boolean) => void;
  setHighlightZone: (value: PrescriptionCreateState['highlightZoneDN']) => void;
  pushZoneHistory: (value: PrescriptionCreateState['zones']) => void;
  popZoneHistory: () => void;
  clearZoneHistory: () => void;
  setCalculatedByOption: (value: 'hectares' | '100l' | 'tank') => void;
  reset: () => void;
  resetProducts: () => void;
  resetZones: () => void;
  setResetStates: (states: 'productsWereReset' | 'zonesWereReset', value: boolean) => void;
}

const initialState: PrescriptionCreateState = {
  productState: 'Granular',
  step: 0,
  product: undefined,
  prescriptionType: undefined,
  fields: [],
  assignee: undefined,
  zoneMethod: 'color',
  colorMap: 'custom',
  customColorSliderValues: [],
  visualizeMode: 'zones',
  uploadedFile: null,
  zones: undefined,
  zonesPaletteKey: undefined,
  zonesHistory: [],
  zonesProductRate: {},
  productUnit: PRESCRIPTIONS_PRODUCT_UNITS['metric'].kg,
  calculatedByOption: 'hectares',
  products: new Map<string | undefined, ProductInfo>([[undefined, { status: 'idle' }]]),
  visualizedSteps: {
    type_and_location: false,
    zone_creation: false,
    zone_rates: false,
    products: false,
    review: false,
  },
  productsWereReset: false,
  zonesWereReset: false,
};

const disableVariability = (fields: Field[]) => {
  if (fields.length > 1) {
    store.dispatch(Actions.setMapViewMode('map', 'map'));
    store.dispatch(Actions.setMapViewMode('map', 'sideMap'));
  }
};

export const usePrescriptionCreateStore = create<
  PrescriptionCreateState & PrescriptionCreateActions
>((set, get) => ({
  ...initialState,
  setProductState: (state) => set(() => ({ productState: state })),
  setType: (type) => set(() => ({ prescriptionType: type, product: undefined })),
  changeProduct: (product, oldProduct) =>
    set((state) => {
      state.productsWereReset = false;
      if (state.products.has(oldProduct?.id)) {
        const newMap = new Map();
        for (const [key, value] of state.products) {
          if (key === oldProduct?.id) {
            newMap.set(product?.id, {
              ...value,
              product: product,
            });
          } else {
            newMap.set(key, value);
          }
        }
        return {
          products: newMap,
        };
      }
      return {
        products: state.products,
      };
    }),
  changeProductValue: (productId, newRate, newTotalQuantity) =>
    set((state) => {
      const product = state.products.get(productId);
      if (product) {
        product.rate = newRate;
        product.totalQuantity = newTotalQuantity;
      }
      return {
        products: state.products,
      };
    }),
  setUploadedFile: (file) => set(() => ({ uploadedFile: file })),
  changeProductStatus: (productId, newStatus) =>
    set((state) => {
      const productInfo = state.products.get(productId);
      if (productInfo) {
        productInfo.status = newStatus;
      }
      return {
        products: state.products,
      };
    }),
  addProduct: () =>
    set((state) => {
      state.products.set(undefined, {
        status: 'idle',
      });
      return {
        products: state.products,
      };
    }),
  removeProduct: (productId) =>
    set((state) => {
      state.products.delete(productId);

      return {
        products: state.products,
      };
    }),
  setProductUnit: (productUnit) => set(() => ({ productUnit })),
  setFields: (fields) => {
    disableVariability(fields);
    set(() => ({ fields }));
  },
  toggleField: (field) => {
    const isFieldSelected = get().fields.some((item) => item.id === field.id);
    if (!isFieldSelected && !field.activeSeason) return;

    set((state) => {
      const newFieldsState = isFieldSelected
        ? state.fields.filter((f) => f.id !== field.id)
        : [...state.fields, field];

      disableVariability(newFieldsState);
      return { fields: newFieldsState };
    });
  },
  setAssignee: (assignee) => set(() => ({ assignee })),
  setZonesProductRate: (zonesProductRate) =>
    set(() => ({
      zonesProductRate,
      zonesWereReset: false,
    })),
  setZoneMethod: (value) =>
    set(() => ({ zoneMethod: value, zones: undefined, zonesPaletteKey: undefined })),
  setColorMap: (value) => set(() => ({ colorMap: value })),
  setCustomColorMap: (value) => set(() => ({ customColorMap: value })),
  setCustomColorSliderValues: (value) => set(() => ({ customColorSliderValues: value })),
  setZones: (value) => set(() => ({ zones: value })),
  setGeneratedZones: (value, paletteKey) =>
    set(() => ({
      zones: value,
      zonesPaletteKey: paletteKey,
      zonesHistory: [],
      zonesProductRate: {},
      highlightZoneDN: null,
    })),
  setVisualizeMode: (value) => set(() => ({ visualizeMode: value })),
  setVisualizedSteps: (step, value) =>
    set((state) => ({
      visualizedSteps: {
        ...state.visualizedSteps,
        [step]: value,
      },
    })),
  setHighlightZone: (value) => set(() => ({ highlightZoneDN: value })),
  pushZoneHistory: (value) => set((state) => ({ zonesHistory: [...state.zonesHistory, value] })),
  popZoneHistory: () =>
    set((state) => {
      const newZones = state.zonesHistory.slice(0, -1);
      return { zonesHistory: newZones, zones: state.zonesHistory.at(-1) };
    }),
  clearZoneHistory: () =>
    set((state) => {
      return {
        zonesHistory: [],
        zones: state.zonesHistory.length > 0 ? state.zonesHistory[0] : state.zones,
      };
    }),
  goBack: () => {
    if (document.startViewTransition) {
      document.startViewTransition(() => set((state) => ({ step: Math.max(0, state.step - 1) })));
    } else {
      set((state) => ({ step: Math.max(0, state.step - 1) }));
    }
  },
  goNext: () => {
    if (document.startViewTransition) {
      document.startViewTransition(() =>
        set((state) => ({
          step: Math.min(4, state.step + 1),
          visualizedSteps: {
            ...state.visualizedSteps,
            [CREATE_STEPS[Math.min(4, state.step + 1)]]: true,
          },
        }))
      );
    } else {
      set((state) => ({
        step: Math.min(4, state.step + 1),
        visualizedSteps: {
          ...state.visualizedSteps,
          [CREATE_STEPS[Math.min(4, state.step + 1)]]: true,
        },
      }));
    }
  },
  goToStep: (step: number) => {
    if (document.startViewTransition) {
      document.startViewTransition(() => set(() => ({ step })));
    } else {
      set(() => ({ step }));
    }
  },
  setCalculatedByOption: (value) => set(() => ({ calculatedByOption: value })),
  reset: () => set(initialState),
  resetProducts: () =>
    set(() => ({
      products: new Map<string | undefined, ProductInfo>([[undefined, { status: 'idle' }]]),
      productsWereReset: true,
    })),
  setProductsList: (productsMap: Map<string | undefined, ProductInfo>) =>
    set(() => ({ products: productsMap })),
  resetZones: () =>
    set(() => ({
      zones: undefined,
      zonesPaletteKey: undefined,
      zonesHistory: [],
      zonesProductRate: {},
      highlightZoneDN: null,
    })),
  setResetStates: (stateToReset: 'productsWereReset' | 'zonesWereReset', value: boolean) =>
    set(() => ({
      [stateToReset]: value,
    })),
}));

/**
 * True once a prescription has meaningful in-progress data (used to guard
 * leaving the create/edit flow with unsaved changes). Read live from the store
 * so save/back paths that `reset()` synchronously before navigating aren't
 * blocked one render late.
 */
export const hasUnsavedPrescription = () => {
  const { step, prescriptionType, fields, zones } = usePrescriptionCreateStore.getState();
  return (
    step > 0 || prescriptionType !== undefined || fields.length > 0 || (zones?.length ?? 0) > 0
  );
};
